import { useState, useRef, useCallback } from "react";
import { ChevronDown } from "lucide-react";

export const COMMON_UNITS = [
  "Liters",
  "Kg",
  "Bags",
  "Drums",
  "Kegs",
  "Buckets",
  "20L Buckets",
  "Cartons",
  "Pcs",
];

interface Props {
  value: string;
  onChange: (val: string) => void;
  className?: string;
  autoFocus?: boolean;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  onBlur?: () => void;
  placeholder?: string;
}

export default function UnitCombobox({
  value,
  onChange,
  className = "",
  autoFocus,
  onKeyDown,
  onBlur,
  placeholder = "Unit",
}: Props) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const displayUnits = value
    ? COMMON_UNITS.filter((u) => u.toLowerCase().includes(value.toLowerCase()))
    : COMMON_UNITS;
  const showDropdown = open && displayUnits.length > 0;

  const select = useCallback(
    (unit: string) => {
      onChange(unit);
      setOpen(false);
      inputRef.current?.focus();
    },
    [onChange]
  );

  const handleKey = (e: React.KeyboardEvent) => {
    if (!open) {
      onKeyDown?.(e);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, displayUnits.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter" && highlight >= 0) {
      e.preventDefault();
      select(displayUnits[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
    } else {
      onKeyDown?.(e);
    }
  };

  return (
    <div className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={value}
          placeholder={placeholder}
          autoFocus={autoFocus}
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(true);
            setHighlight(0);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            setTimeout(() => {
              setOpen(false);
              onBlur?.();
            }, 150);
          }}
          onKeyDown={handleKey}
          className={`w-full bg-gray-50 dark:bg-navy-900 border border-light-border dark:border-navy-700 rounded-xl px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:border-citilux-blue-500 pr-8 ${className}`}
        />
        <ChevronDown
          className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-metallic-silver pointer-events-none"
        />
      </div>
      {showDropdown && (
        <div
          ref={listRef}
          className="absolute z-50 top-full mt-1 left-0 right-0 bg-white dark:bg-navy-800 border border-light-border dark:border-navy-700 rounded-xl shadow-lg dark:shadow-black/30 overflow-hidden"
        >
          {displayUnits.map((unit, idx) => (
            <button
              key={unit}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => select(unit)}
              onMouseEnter={() => setHighlight(idx)}
              className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                idx === highlight
                  ? "bg-citilux-blue-600/20 text-citilux-blue-400"
                  : "text-gray-800 dark:text-white hover:bg-gray-100 dark:hover:bg-navy-700"
              }`}
            >
              {unit}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
